import { useCallback } from 'react';
import { useAuth } from '@repletoryreach/sdk-auth';
import { identity } from '@repletoryreach/sdk-api';
import { useI18n } from '../i18n/I18nProvider';
import type { Locale } from '../i18n/catalog';

/**
 * The one place the console's language is read and written.
 *
 * `settings-appearance.md` §4 item 1: there were TWO language pickers that did
 * not agree. Settings → Appearance set `useI18n().locale`, which is client
 * state only; Settings → Profile set `user.locale` through the API. Changing
 * one did not change the other, so a user who set their language in Appearance
 * saw it revert on a new device, a user who set it in Profile might not see
 * the console change at all, and the two pickers offered different option
 * lists (Profile carried its own hardcoded five-locale array).
 *
 * This writes both: the client store immediately, so the UI turns over at
 * once, and the server in the background, so the choice follows the user to
 * their next device. A failed write is deliberately not surfaced as an error:
 * the language HAS changed for this session, and a toast saying otherwise
 * would contradict what the user can see.
 */
export function useLocalePreference(): {
  locale: Locale;
  setLocale: (next: Locale) => void;
} {
  const { locale, setLocale: setClientLocale } = useI18n();
  const { user, refresh } = useAuth();

  const setLocale = useCallback((next: Locale) => {
    setClientLocale(next);
    if (!user) return;
    void identity.updateMyLocale(next)
      // Keep the session's own copy in step, so a page that reads
      // `user.locale` (the profile form) does not show the old value.
      .then(() => refresh())
      .catch(() => { /* the session keeps the new language regardless */ });
  }, [setClientLocale, user, refresh]);

  return { locale, setLocale };
}
